import React, { useCallback, useState } from 'react';
import { deleteTargetingTemplate, updateTargetingTemplate } from '../../../api/MediaPlanTargetings.api';
import { Form, Formik, FormikHelpers, Field } from 'formik';
import Modal, { ModalContent, ModalFooter, ModalTitle } from '../../../../../../ui/General/Modal/Modal';
import FormField from '../../../../../../ui/DataEntry/FormField/FormField';
import Label from '../../../../../../ui/DataEntry/Label/Label';
import styles from './TargetingTemplateModal.module.css';
import cn from 'classnames';
import TextArea from '../../../../../../SharedComponents/Forms/TextArea/TextArea';
import Button from '../../../../../../ui/Buttons/Button/Button';
import { showNotification } from '../../../../../../helpers';
import { TargetingTemplate } from '../../../types/MediaPlanTargeting.types';

type TargetingTemplateModalProps = {
    template: TargetingTemplate;
    isModalOpen: boolean;
    closeModal: () => void;
};

const TargetingTemplateModal = ({ template, isModalOpen, closeModal }: TargetingTemplateModalProps) => {
    const [showDiscardChangesPrompt, setShowDiscardChangesPrompt] = useState(false);
    const [errorSubmitting, setErrorSubmitting] = useState(false);
    const [showDeleteTargetingTemplatePrompt, setShowDeleteTargetingTemplatePrompt] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);

    const handleSubmit = useCallback(
        async (values: TargetingTemplate, { setSubmitting, resetForm }: FormikHelpers<TargetingTemplate>) => {
            try {
                setSubmitting(true);
                setErrorSubmitting(false);
                await updateTargetingTemplate(values);
                resetForm({ values });
                showNotification('Targeting template updated', 'info');
            } catch {
                setErrorSubmitting(true);
            } finally {
                setSubmitting(false);
            }
        },
        []
    );

    const handleDelete = useCallback(async () => {
        try {
            setDeleteLoading(true);
            await deleteTargetingTemplate(template.id);
            setDeleteLoading(false);
            setShowDeleteTargetingTemplatePrompt(false);
            closeModal();
            showNotification(`Targeting template "${template.template_name}" has been deleted`, 'info');
        } catch {
            showNotification('Could not delete targeting template', 'error');
        } finally {
            setDeleteLoading(false);
            setShowDeleteTargetingTemplatePrompt(false);
        }
    }, [closeModal, template.id, template.template_name]);

    return (
        <>
            <Modal
                className={styles.deleteTargetingTemplate}
                closeOnOverlayClick
                isOpen={showDeleteTargetingTemplatePrompt}
                onClose={() => setShowDeleteTargetingTemplatePrompt(false)}
            >
                <ModalTitle>Delete "{template.template_name}" targeting template?</ModalTitle>
                <ModalContent>
                    <p>
                        You're going to delete "{template.template_name}" targeting template. This operation cannot be
                        undone.
                    </p>
                </ModalContent>
                <ModalFooter align="center">
                    <Button type="bordered" color="negative" loading={deleteLoading} onClick={handleDelete}>
                        Delete targeting
                    </Button>
                </ModalFooter>
            </Modal>

            <Formik enableReinitialize initialValues={template} onSubmit={handleSubmit}>
                {({ dirty, submitForm, isSubmitting, resetForm }) => (
                    <>
                        <Modal
                            className={styles.discardChanges}
                            isOpen={showDiscardChangesPrompt}
                            onClose={() => setShowDiscardChangesPrompt(false)}
                        >
                            <ModalTitle>Discard changes?</ModalTitle>
                            <ModalContent className={styles.discardChangesContent}>
                                <p>This operation cannot be undone</p>
                            </ModalContent>
                            <ModalFooter>
                                <Button
                                    type="filled"
                                    color="black"
                                    onClick={() => {
                                        resetForm();
                                        setShowDiscardChangesPrompt(false);
                                        closeModal();
                                    }}
                                >
                                    Discard
                                </Button>
                            </ModalFooter>
                        </Modal>

                        <Modal
                            closeOnOverlayClick
                            isOpen={isModalOpen}
                            onClose={() => {
                                if (!dirty) {
                                    closeModal();
                                    return;
                                }

                                setShowDiscardChangesPrompt(true);
                            }}
                        >
                            <ModalTitle>Edit Targeting Template</ModalTitle>
                            <ModalContent className={styles.modalContent}>
                                {errorSubmitting && <div>Error creating targeting template</div>}
                                <Form className={styles.form}>
                                    <div className={cn(styles.row, styles.evenFields)}>
                                        <FormField>
                                            <Label htmlFor="template_name">Template name</Label>
                                            <Field
                                                name="template_name"
                                                id="template_name"
                                                placeholder="Enter template name"
                                            />
                                        </FormField>

                                        <FormField>
                                            <Label htmlFor="targeting_name">Targeting name</Label>
                                            <Field
                                                name="targeting_name"
                                                id="targeting_name"
                                                placeholder="Enter targeting name"
                                            />
                                        </FormField>
                                    </div>

                                    <div className={cn(styles.row, styles.evenFields)}>
                                        <FormField>
                                            <Label htmlFor="min_age">Min Age</Label>
                                            <Field
                                                type="number"
                                                id="min_age"
                                                name="min_age"
                                                placeholder="Enter min age"
                                            />
                                        </FormField>

                                        <FormField>
                                            <Label htmlFor="max_age">Max Age</Label>
                                            <Field
                                                type="number"
                                                id="max_age"
                                                name="max_age"
                                                placeholder="Enter max age"
                                            />
                                        </FormField>
                                    </div>

                                    <FormField>
                                        <Label htmlFor="location">Location</Label>
                                        <Field id="location" name="location" placeholder="Enter location" />
                                    </FormField>

                                    <div>
                                        <Label className={styles.genderLabel} htmlFor="">
                                            Gender
                                        </Label>
                                        <div className={styles.row}>
                                            <FormField className={styles.inline}>
                                                <Field type="checkbox" name="is_male" id="is_male" />
                                                <Label htmlFor="is_male">Male</Label>
                                            </FormField>

                                            <FormField className={styles.inline}>
                                                <Field type="checkbox" name="is_female" id="is_female" />
                                                <Label htmlFor="is_female">Female</Label>
                                            </FormField>
                                        </div>
                                    </div>

                                    <FormField>
                                        <Label htmlFor="remarketing_audience">Remarketing Audience</Label>
                                        <Field
                                            id="remarketing_audience"
                                            name="remarketing_audience"
                                            placeholder="Enter remarketing audience"
                                        />
                                    </FormField>

                                    <FormField>
                                        <Label htmlFor="keywords">Keywords</Label>
                                        <Field id="keywords" name="keywords" placeholder="Enter keywords" />
                                    </FormField>

                                    <FormField>
                                        <Label htmlFor="notes">Notes</Label>
                                        <TextArea name="notes" placeholder="Write notes" />
                                    </FormField>
                                </Form>
                            </ModalContent>
                            <ModalFooter>
                                <Button
                                    disabled={!dirty}
                                    loading={isSubmitting}
                                    className={styles.submit}
                                    htmlType="submit"
                                    onClick={submitForm}
                                    type="filled"
                                    color="black"
                                >
                                    Save targeting
                                </Button>

                                <Button
                                    type="filled"
                                    color="negative"
                                    onClick={() => setShowDeleteTargetingTemplatePrompt(true)}
                                >
                                    Delete
                                </Button>
                            </ModalFooter>
                        </Modal>
                    </>
                )}
            </Formik>
        </>
    );
};

export default TargetingTemplateModal;
